import uPlot from "uplot";

import { columnHighlight } from "../plugins/columnHighlight";
import { selected } from "../plugins/selected";
import { seriesColor } from "./palette";

const COLORS = {
  // UI colors
  gray100: "#f3f4f6",
  gray200: "#e5e7eb",
  gray400: "#9ca3af",
  white: "#fff",
  indigo500Alpha: "rgba(99, 102, 241, 0.06)",
};

function fillGradient(hexColor) {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  return (u) => {
    const { top, height } = u.bbox;
    if (!isFinite(top) || !isFinite(height) || height === 0) {
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    }
    const grd = u.ctx.createLinearGradient(0, top, 0, top + height);
    grd.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.2)`);
    grd.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0.01)`);
    return grd;
  };
}

export function createSeriesFromLabels(labels, opts = {}, { fill = true } = {}) {
  // Multi-series charts ship `labels` as a positional array so the order
  // lines up with the data array. Single-series charts still pass a
  // `{key: "Display Name"}` Map; Object.values handles both.
  const values = Array.isArray(labels) ? labels : Object.values(labels);
  // Multi-series charts (e.g. fulfillment by platform) become unreadable when
  // every line carries its own translucent fill — the stacked alphas turn the
  // plot to mud. Reserve the gradient fill for single-series charts, and let
  // callers force it off entirely for unit-aware contexts (rates/percentages
  // where a filled area implies a stock metric).
  const useFill = fill && values.length <= 1;

  return values.map((label, idx) => {
    const color = seriesColor(idx);
    return {
      label,
      show: true,
      spanGaps: true,
      stroke: color,
      fill: useFill ? fillGradient(color) : undefined,
      width: 2,
      ...opts,
    };
  });
}

function getYAxisLabel(labels, override) {
  if (override) return override;
  const labelKey = Object.keys(labels)[0] || "";
  if (!labelKey || !labels[labelKey]) return "Count";
  if (labels[labelKey].includes("Peak Concurrent")) return "Concurrent Sessions";
  if (labels[labelKey].includes("Unique Users")) return "Active Users";
  return "Sessions";
}

// `valueMeta` is [seriesIdx][dataIdx] -> [numerator, denominator], parallel to
// the plotted series. A percentage alone can't tell 1-of-1 from 340-of-412, and
// on these charts that is the difference between a real regression and a day
// with two sessions in it.
function valueFormatter(valueMeta, seriesIdx) {
  const series = valueMeta[seriesIdx - 1];
  if (!series) return undefined;

  return (u, rawValue, _seriesIdx, idx) => {
    if (rawValue === null || rawValue === undefined) return "--";

    const pair = series[idx];
    if (!pair) return rawValue;

    return `${rawValue} (${pair[0]}/${pair[1]})`;
  };
}

export function usage({
  labels,
  unit,
  onSelectedIndex,
  scale_y_min,
  scale_y_max,
  scale_y_max_lower_bound,
  axis_y_integers_only,
  y_axis_label,
  value_meta,
}) {
  const min = scale_y_min !== undefined ? scale_y_min : 0;
  const max = scale_y_max !== undefined ? scale_y_max : null;
  // Rate/percentage metrics are not stock quantities, so a filled area under
  // the line reads as misleading. Disable fill for "%"-unit charts.
  const seriesFillOpts = { fill: unit !== "%" };

  return {
    plugins: [
      columnHighlight({ style: { backgroundColor: COLORS.indigo500Alpha } }),
      selected({ onSelectedIndex }),
    ],
    width: 800,
    height: 400,
    padding: [16, 12, 12, 0],
    // Every x-value is a UTC midnight, so the axis has to read them in UTC.
    // uPlot's default tzDate renders in the browser's zone, which labels a
    // UTC midnight as the previous day anywhere west of UTC — and then the
    // server resolves a click on it back to the day the label denied.
    tzDate: (ts) => uPlot.tzDate(new Date(ts * 1e3), "Etc/UTC"),
    scales: {
      x: {
        time: true,
      },
      y: {
        // Keep auto:true so uPlot derives min0/max0 from the data and feeds
        // them into our range function. When auto was conditionally turned
        // off (because the caller supplied an explicit `max`), uPlot stopped
        // computing bounds and the range function received undefined max0,
        // collapsing the y-axis to a tiny near-zero band.
        auto: true,
        range: (_u, _min0, max0) => {
          let upper = max !== null ? max : max0;

          if (scale_y_max_lower_bound !== null && upper < scale_y_max_lower_bound) {
            upper = scale_y_max_lower_bound;
          }

          return [min, upper];
        },
      },
    },
    axes: [
      {
        scale: "x",
        labelSize: 0,
        gap: 6,
        size: 32,
        stroke: COLORS.gray400,
        font: "12px ui-sans-serif, system-ui, sans-serif",
        ticks: { stroke: COLORS.gray200, width: 1, size: 4 },
        grid: { stroke: COLORS.gray100, width: 1 },
        timeFormat: "%b %d",
      },
      {
        scale: "y",
        label: getYAxisLabel(labels, y_axis_label),
        labelSize: 18,
        gap: 6,
        size: 52,
        stroke: COLORS.gray400,
        font: "12px ui-sans-serif, system-ui, sans-serif",
        ticks: { stroke: COLORS.gray200, width: 1, size: 4 },
        grid: { stroke: COLORS.gray100, width: 1 },
        values: axis_y_integers_only ? (u, vals) => vals.map((v) => (v % 1 ? null : v)) : undefined,
      },
    ],
    series: [
      {
        show: true,
        scale: "x",
      },
      ...createSeriesFromLabels(
        labels,
        {
          points: {
            show: (u, seriesIdx, idx0, idx1) => idx1 - idx0 <= 14,
            size: 6,
            fill: COLORS.white,
            width: 2,
          },
        },
        seriesFillOpts,
      ).map((series, idx) =>
        value_meta ? { ...series, value: valueFormatter(value_meta, idx + 1) } : series,
      ),
    ],
    legend: {
      show: true,
      // uPlot XORs this against ctrl/meta, so `true` makes a plain click
      // "show only this series" and cmd-click "toggle just this one". Picking
      // two platforms out of a dozen is two clicks instead of ten un-checks.
      isolate: true,
    },
    hooks: {
      setUnit(uplot) {
        uplot.setScale("y", { min: 0, max: null });
      },

      setData(_uplot) {},
    },
  };
}
